export type ServiceEstimateExampleKey =
  | "start"
  | "growth"
  | "operator"
  | "migration_cleanup";

export type ServiceEstimateExampleLocale = "sv" | "en";

export type ServiceEstimateExampleProfile = {
  monthly_transaction_count: number;
  monthly_sales_invoice_count: number;
  monthly_purchase_receipt_count: number;
  bank_account_count: number;
  bank_sources: Array<"bank_file" | "revolut" | "manual_upload" | "future_bank_integration">;
  vat_period: "none" | "monthly" | "quarterly" | "yearly";
  needs_ap_ar: boolean;
  needs_fixed_assets: boolean;
  fixed_asset_count: number;
  needs_sie_migration: boolean;
  cleanup_backlog_months: number;
  wants_bokslut: boolean;
  reporting_needs: string[];
};

export type ServiceEstimateExample = {
  key: ServiceEstimateExampleKey;
  servicePackage: ServiceEstimateExampleKey;
  assetId: string;
  title: Record<ServiceEstimateExampleLocale, string>;
  body: Record<ServiceEstimateExampleLocale, string>;
  signals: Record<ServiceEstimateExampleLocale, string[]>;
  monthlyRangeSek: readonly [number, number];
  setupRangeSek: readonly [number, number];
  estimateProfile: ServiceEstimateExampleProfile;
  deliveryPreference: "self_service" | "human_assisted" | "hybrid";
};

export const serviceEstimateExampleContent = {
  sv: {
    gridLabel: "Exempel på agentiska serviceestimat",
    monthlyRangeLabel: "Månadstjänst",
    setupRangeLabel: "Startarbete",
    signalsLabel: "Typiska signaler",
    nonBindingNote:
      "Exemplen är icke bindande och separata från programabonnemangen. Slutligt upplägg bekräftas efter genomgång.",
    creditUnitNote:
      "Agent credits visas inte som fasta exempelvärden. Kreditbehov för nya offerter beräknas från valda tjänster och deklarerad arbetsvolym med den aktuella, evidensstyrda marknadsestimatorn och den livekonfigurerade Eregis Credit-katalogen. DynamicBooks skapar ingen checkout, fakturerar inte direkt och ändrar inte abonnemangsstatus.",
  },
  en: {
    gridLabel: "Agentic service estimate examples",
    monthlyRangeLabel: "Monthly service",
    setupRangeLabel: "Setup work",
    signalsLabel: "Typical signals",
    nonBindingNote:
      "Examples are non-binding and separate from software subscriptions. The final setup is confirmed after source and scope checks.",
    creditUnitNote:
      "Agent credits are not shown as fixed example values. Credit capacity for new proposals is calculated from selected services and declared workload using the current evidence-gated market estimator and live Eregis Credit catalog. DynamicBooks does not create checkout, bill directly, or mutate subscription state.",
  },
} as const;

export const serviceEstimateExamples: ServiceEstimateExample[] = [
  {
    key: "start",
    servicePackage: "start",
    assetId: "pricing-start",
    title: {
      sv: "Start",
      en: "Start",
    },
    body: {
      sv: "För mindre bolag som vill få löpande bokföring, moms och enkla rapporter på plats utan ett stort startprojekt.",
      en: "For smaller companies that need recurring bookkeeping, VAT, and simple reporting without a large onboarding project.",
    },
    signals: {
      sv: ["Upp till cirka 75 transaktioner per månad", "En bankkälla", "Årlig moms eller ingen moms"],
      en: ["Up to around 75 transactions per month", "One bank source", "Yearly VAT or no VAT"],
    },
    monthlyRangeSek: [1490, 3900],
    setupRangeSek: [0, 2900],
    deliveryPreference: "self_service",
    estimateProfile: {
      monthly_transaction_count: 75,
      monthly_sales_invoice_count: 4,
      monthly_purchase_receipt_count: 12,
      bank_account_count: 1,
      bank_sources: ["bank_file"],
      vat_period: "yearly",
      needs_ap_ar: false,
      needs_fixed_assets: false,
      fixed_asset_count: 0,
      needs_sie_migration: false,
      cleanup_backlog_months: 0,
      wants_bokslut: false,
      reporting_needs: [],
    },
  },
  {
    key: "growth",
    servicePackage: "growth",
    assetId: "pricing-growth",
    title: {
      sv: "Tillväxt",
      en: "Growth",
    },
    body: {
      sv: "För team som har fler underlag, månads- eller kvartalsmoms och behöver tydligare avstämning mellan fakturor, kvitton och bank.",
      en: "For teams with more source documents, monthly or quarterly VAT, and clearer reconciliation needs across invoices, receipts, and bank activity.",
    },
    signals: {
      sv: ["Cirka 75-400 transaktioner per månad", "Kund- och leverantörsreskontra", "Periodisk rapportering"],
      en: ["Around 75-400 transactions per month", "Accounts receivable and payable", "Recurring reporting"],
    },
    monthlyRangeSek: [3900, 9900],
    setupRangeSek: [2900, 9900],
    deliveryPreference: "hybrid",
    estimateProfile: {
      monthly_transaction_count: 120,
      monthly_sales_invoice_count: 14,
      monthly_purchase_receipt_count: 35,
      bank_account_count: 2,
      bank_sources: ["bank_file", "revolut"],
      vat_period: "monthly",
      needs_ap_ar: true,
      needs_fixed_assets: false,
      fixed_asset_count: 0,
      needs_sie_migration: false,
      cleanup_backlog_months: 0,
      wants_bokslut: false,
      reporting_needs: ["profit_loss", "balance_sheet"],
    },
  },
  {
    key: "operator",
    servicePackage: "operator",
    assetId: "pricing-operator",
    title: {
      sv: "Operatör",
      en: "Operator",
    },
    body: {
      sv: "För verksamheter där flera bankflöden, anläggningar och ledningsrapportering gör bokföringen mer operativ vecka för vecka.",
      en: "For companies where several bank flows, fixed assets, and management reporting make bookkeeping a more operational weekly workflow.",
    },
    signals: {
      sv: ["Cirka 400-1 000 transaktioner per månad", "Flera bankkonton", "Anläggningar och ledningsrapportering"],
      en: ["Around 400-1,000 transactions per month", "Multiple bank accounts", "Fixed assets and management reporting"],
    },
    monthlyRangeSek: [9900, 24900],
    setupRangeSek: [9900, 29900],
    deliveryPreference: "hybrid",
    estimateProfile: {
      monthly_transaction_count: 520,
      monthly_sales_invoice_count: 34,
      monthly_purchase_receipt_count: 80,
      bank_account_count: 3,
      bank_sources: ["bank_file", "revolut", "manual_upload"],
      vat_period: "monthly",
      needs_ap_ar: true,
      needs_fixed_assets: true,
      fixed_asset_count: 8,
      needs_sie_migration: false,
      cleanup_backlog_months: 0,
      wants_bokslut: false,
      reporting_needs: ["management_package"],
    },
  },
  {
    key: "migration_cleanup",
    servicePackage: "migration_cleanup",
    assetId: "pricing-cleanup",
    title: {
      sv: "Migrering och städning",
      en: "Migration/Cleanup",
    },
    body: {
      sv: "För bolag som flyttar historik, städar backlogg eller kombinerar löpande arbete med årsslut och manuell kvalitetssäkring.",
      en: "For companies moving history, cleaning up a backlog, or combining recurring work with year-end support and guided onboarding.",
    },
    signals: {
      sv: ["Historisk import eller backlogg", "Bokslutsstöd", "Hybrid leverans med kontrollpunkter"],
      en: ["Historical import or backlog", "Year-end support", "Hybrid delivery with checkpoints"],
    },
    monthlyRangeSek: [4900, 19900],
    setupRangeSek: [12900, 79900],
    deliveryPreference: "hybrid",
    estimateProfile: {
      monthly_transaction_count: 180,
      monthly_sales_invoice_count: 18,
      monthly_purchase_receipt_count: 44,
      bank_account_count: 2,
      bank_sources: ["bank_file"],
      vat_period: "monthly",
      needs_ap_ar: true,
      needs_fixed_assets: false,
      fixed_asset_count: 0,
      needs_sie_migration: true,
      cleanup_backlog_months: 6,
      wants_bokslut: true,
      reporting_needs: ["annual_artifacts"],
    },
  },
];

function formatNumber(value: number, locale: ServiceEstimateExampleLocale) {
  return new Intl.NumberFormat(locale === "sv" ? "sv-SE" : "en-US", {
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace(/\u00a0/g, " ");
}

function formatRange(
  range: readonly [number, number],
  locale: ServiceEstimateExampleLocale,
  compact = false,
) {
  const [min, max] = range;
  if (min === max) {
    return compact ? formatCompactNumber(min, locale) : formatNumber(min, locale);
  }

  const formatter = compact ? formatCompactNumber : formatNumber;
  return `${formatter(min, locale)}-${formatter(max, locale)}`;
}

function formatCompactNumber(value: number, locale: ServiceEstimateExampleLocale) {
  if (Math.abs(value) < 1000) {
    return formatNumber(value, locale);
  }

  const amount = value / 1000;
  const maximumFractionDigits = Number.isInteger(amount) ? 0 : 1;
  const formatted = new Intl.NumberFormat(locale === "sv" ? "sv-SE" : "en-US", {
    maximumFractionDigits,
  })
    .format(amount)
    .replace(/\u00a0/g, " ");

  return `${formatted}K`;
}

export function formatSekRange(
  range: readonly [number, number],
  locale: ServiceEstimateExampleLocale,
  cadence: "monthly" | "setup",
  options: { compact?: boolean } = {},
) {
  const suffix =
    locale === "sv"
      ? cadence === "monthly"
        ? "SEK/månad"
        : "SEK start"
      : cadence === "monthly"
        ? "SEK/month"
        : "SEK setup";

  return `${formatRange(range, locale, options.compact)} ${suffix}`;
}
